import React, { useCallback, useEffect, useState } from 'react';
import './welcome.css';
import { journey, profile } from '../../data/profile';

const SESSION_KEY = 'welcome-played';

// Keep in sync with the animation delays in welcome.css.
const EXIT_AT = 4200;
const UNMOUNT_AT = 4900;

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function shouldPlay() {
  if (typeof window === 'undefined') return false;
  if (prefersReducedMotion()) return false;
  try {
    return window.sessionStorage.getItem(SESSION_KEY) !== 'true';
  } catch (error) {
    // Private browsing can throw on sessionStorage access — just play it.
    return true;
  }
}

function Welcome() {
  const [playing, setPlaying] = useState(shouldPlay);
  const [leaving, setLeaving] = useState(false);

  const finish = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => setPlaying(false), UNMOUNT_AT - EXIT_AT);
  }, []);

  // Hold the page still — and hold the hero's entrance — while this plays.
  useEffect(() => {
    if (!playing) return undefined;

    document.documentElement.classList.add('intro-active');
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.classList.remove('intro-active');
      document.body.style.overflow = '';
    };
  }, [playing]);

  useEffect(() => {
    if (!playing) return undefined;

    try {
      window.sessionStorage.setItem(SESSION_KEY, 'true');
    } catch (error) {
      /* nothing to do — the animation just replays next visit */
    }

    const exitTimer = window.setTimeout(finish, EXIT_AT);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') finish();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(exitTimer);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [playing, finish]);

  if (!playing) return null;

  return (
    <div className={`welcome${leaving ? ' welcome--leaving' : ''}`} role="status" aria-label="Welcome">
      <div className="welcome-inner">
        <p className="welcome-greeting">
          <span className="welcome-greeting-native">नमस्ते</span>
          <span className="welcome-greeting-en">Welcome</span>
        </p>

        {/* Deliberately not an <h1> — the hero owns the page's real heading. */}
        <p className="welcome-name">{profile.name}</p>
        <p className="welcome-headline">{profile.headline}</p>

        <ol className="journey">
          <span className="journey-rail" aria-hidden="true" />
          {journey.map(({ year, place, role }, index) => (
            <li
              className="journey-stop"
              key={`${year}-${place}-${role}`}
              style={{ '--stop-index': index }}
            >
              <span className="journey-dot" aria-hidden="true" />
              <span className="journey-year">{year}</span>
              <span className="journey-place">{place}</span>
              <span className="journey-role">{role}</span>
            </li>
          ))}
        </ol>

        <p className="welcome-closing">…and still building.</p>
      </div>

      <button type="button" className="welcome-skip" onClick={finish}>
        Skip intro
      </button>
    </div>
  );
}

export default Welcome;
